import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import ChevronRight16 from '@carbon/icons-react/es/chevron--right/16';
import TreeView16 from '@carbon/icons-react/es/tree-view/16';
import Typography from 'antd/lib/typography';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { StatusGroup } from 'api/tasks';
import { ReactComponent as Sprint } from 'assets/svgs/all-sprints.svg';
import { Button } from 'components/atoms/button';
import {
  CustomDueDatePicker,
  DateValue,
} from 'components/atoms/due-date-picker';
import { StatusIndicator } from 'components/molecules/status-indicator';
import { Table } from 'components/molecules/table';
import { HelperText } from 'components/styles';
import { GanttType } from '../helpers';
import { TaskConflictRadioOptions } from './date-conflict-modal';
import { Conflict } from './types';

export const StyledTable = styled(Table)`
  margin-top: var(--spacing-05);
  td.phase {
    display: flex;
  }

  .ant-table-tbody > tr > td.start-date {
    padding-left: var(--spacing-05);
  }
  .ant-table-tbody > tr.ant-table-row.ant-table-row-level-0 {
    height: 56px;
  }
  .ant-table-tbody > tr.ant-table-row.ant-table-row-level-1 {
    background: var(--coolest-gray-10);
    height: 48px;
  }
`;

const StyledButton = styled(Button)`
  margin: var(--spacing-03);
`;

interface StyledProps {
  startDateExtended: boolean | undefined;
  dueDateExtended: boolean | undefined;
  isPhase: boolean;
}

const DueDateWrapper = styled.div<StyledProps>`
  span#from {
    color: ${(props) =>
      props.startDateExtended
        ? 'var(--red-60)'
        : !props.isPhase
        ? 'var(--blue-50)'
        : 'inherit'};
  }
  span#to {
    color: ${(props) =>
      props.dueDateExtended
        ? 'var(--red-60)'
        : !props.isPhase
        ? 'var(--blue-50)'
        : 'inherit'};
  }
`;

interface Props {
  dataSource?: Conflict[];
  selected?: TaskConflictRadioOptions;
  handleDateChange?: (newDates: [DateValue, DateValue], record: any) => void;
  dueDateExtendedArray: number[];
  startDateExtendedArray: number[];
  dependencyConflict?: boolean;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

export const TextWrapper = styled.div<{ isPhase: boolean | undefined }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: ${(props) => (props.isPhase ? '56px' : '48px')};
  width: 240px;
  cursor: ${(props) => (props.isPhase ? 'pointer' : 'initial')};
`;

const StatusWrapper = styled.div`
  margin-left: var(--spacing-05);
`;

export const SecondaryText = styled.div`
  ${HelperText};
`;

export const PhasesTable: FC<Props> = ({
  dataSource,
  handleDateChange,
  startDateExtendedArray,
  dueDateExtendedArray,
  dependencyConflict,
}) => {
  const windowHeight = window.innerHeight;
  const modalFooterHeight = 72;
  const modalHeaderHeight = 58;
  const radioOptions = 114;

  const tableHeight =
    windowHeight * 0.7 - radioOptions - modalFooterHeight - modalHeaderHeight;
  const { Text } = Typography;

  if (dataSource == null) {
    return null;
  }

  return (
    <StyledTable
      key={dataSource?.length}
      size="small"
      columns={[
        {
          title: (
            <FormattedMessage id="tasks.date.conflicts.phases_and_tasks" />
          ),
          dataIndex: 'name',
          key: 'name',
          className: 'phase',
          render: (value: string, record: any): React.ReactNode => {
            if (
              (record.type === GanttType.TaskBar ||
                record.type === GanttType.Subtask ||
                record.type === GanttType.Sprint) &&
              !record.status
            ) {
              return <></>;
            }

            return (
              <Container>
                {(record.type === GanttType.TaskBar ||
                  record.type === GanttType.Subtask ||
                  record.type === GanttType.Sprint) &&
                  record.status.metaFieldValue && (
                    <StatusWrapper>
                      <StatusIndicator
                        progress={0}
                        status={{
                          color: record.status.metaFieldValue.color,
                          group: record.status.metaFieldValue
                            .group as StatusGroup,
                          label: record.status.metaFieldValue.label,
                        }}
                      />
                    </StatusWrapper>
                  )}
                {record.type === 'SUBTASK' && <TreeView16 />}
                {record.type === 'SPRINT' && <Sprint />}
                <TextWrapper
                  isPhase={record.type === GanttType.ProjectPhaseBar}
                >
                  <Text ellipsis={{ tooltip: true }}>{value}</Text>
                  {record.type === GanttType.ProjectPhaseBar && (
                    <SecondaryText>
                      <FormattedMessage
                        id="tasks.date_conflicts_phases_tasks_number"
                        values={{
                          numberOfTasks: record.numberOfTasks,
                        }}
                      />
                    </SecondaryText>
                  )}
                </TextWrapper>
              </Container>
            );
          },
        },
        {
          title: <FormattedMessage id="tasks.date.conflicts.current_dates" />,
          dataIndex: 'currentDates',
          key: 'currentDates',
          width: '25%',
          render: (value): React.ReactNode => {
            const [startDate, dueDate] = value;

            return (
              <>
                <CustomDueDatePicker
                  hideIcon
                  disabled
                  isRangePicker
                  id="due_date"
                  size="small"
                  startDate={startDate}
                  dueDate={dueDate}
                />
              </>
            );
          },
        },
        {
          title: <FormattedMessage id="tasks.date.conflicts.new_dates" />,
          dataIndex: 'newDates',
          key: 'newDates',
          width: '25%',
          render: (value: string, record: any): React.ReactNode => {
            const [startDate, dueDate] = value;

            return (
              <DueDateWrapper
                startDateExtended={
                  startDateExtendedArray &&
                  startDateExtendedArray.some((el) => el === record.key)
                }
                dueDateExtended={
                  dueDateExtendedArray &&
                  dueDateExtendedArray.some((el) => el === record.key)
                }
                isPhase={record.type === GanttType.ProjectPhaseBar}
                id="new-dates"
              >
                <CustomDueDatePicker
                  hideIcon
                  disabled={
                    record.type === GanttType.ProjectPhaseBar ||
                    dependencyConflict
                  }
                  id="due_date"
                  size="small"
                  isRangePicker
                  startDate={startDate}
                  dueDate={dueDate}
                  onDateChange={(latest) => {
                    if (handleDateChange) {
                      handleDateChange(latest, record);
                    }
                  }}
                />
              </DueDateWrapper>
            );
          },
        },
      ]}
      sticky
      dataSource={dataSource}
      pagination={false}
      scroll={{
        y: tableHeight,
      }}
      expandable={{
        defaultExpandAllRows: true,
        indentSize: 32,
        expandRowByClick: true,
        expandIcon: ({ expanded, onExpand, record }) =>
          record.children &&
          record.children.length > 0 && (
            <StyledButton
              size="small"
              type="text"
              icon={expanded ? <ChevronDown16 /> : <ChevronRight16 />}
              onClick={() => {
                onExpand(expanded, record);
              }}
            />
          ),
      }}
    />
  );
};
